<template>
  <section>
    <PageTitle>
      <span v-html="$store.state.app.pageName" />
    </PageTitle>

    <SwipeTab
      v-model="currentType"
      @change="onTabChange"
      :items="tabEventTypes"
      :background-color="true"
      :loading="loading"
    />

    <template v-if="isReserved">
      <ReservedEvents
        :reservedEvents="reservedEvents"
        :loading="loading"
        @canceled="reserveCanceled"
      />
    </template>

    <template v-if="isReservable">
      <EventReserveForm
        :event="event"
        :type="currentType"
        :loading="loading"
        @reserve-complete="reserveComplete"
      />
    </template>

    <template v-if="tabEventTypes.length === 0">
      <PageContent>
        <Container>
          <NoticeBox
            type="error"
            :borderless="true"
            class="space-b-2 text-bold"
          >
            現在、予約できるイベントがありません
          </NoticeBox>
          <p class="text-paragraph text-xs">
            恐れ入りますが、現在予約できるイベントがありません。
            <br class="mobile-only" />
            イベントがある場合は、この画面に詳細が表示されます。
          </p>
          <div class="text-center space-t-6">
            <router-link
              :to="$wcRouter.path({ name: 'index' })"
              class="button waves-effect waves-light"
            >
              TOPへ
            </router-link>
          </div>
        </Container>
      </PageContent>
    </template>
  </section>
</template>

<script>
import SwipeTab from '@/components/SwipeTab.vue'
import EventReserveForm from '@/components/EventReserveForm.vue'
import ReservedEvents from '@/components/ReservedEvents.vue'
import NoticeBox from '@/components/NoticeBox.vue'
import { renderError } from '@/utils/error'
import eventApi from '@/api/event'

const EVENT_TYPE_RESERVED = 0

export default {
  components: {
    SwipeTab,
    EventReserveForm,
    ReservedEvents,
    NoticeBox,
  },

  metaInfo() {
    return {
      title: this.$store.state.app.pageName,
      bodyAttrs: { class: 'reservation' }
    }
  },

  data() {
    return {
      eventTypes: [],
      currentType: EVENT_TYPE_RESERVED,
      loading: false,
      event: null,
      reservedEvents: []
    }
  },

  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    tabEventTypes() {
      return this.eventTypes.map(item => ({
        key: item.type,
        label: this.$domUtils.decode(item.title),
      }))
    },
    isReserved() {
      return (
        this.eventTypes.length > 0 &&
        this.currentType === EVENT_TYPE_RESERVED
      )
    },
    isReservable() {
      return (
        this.eventTypes.length > 0 &&
        this.currentType !== EVENT_TYPE_RESERVED
      )
    },
  },

  methods: {
    async init() {
      await this.$withLoading(this.fetchEventTypes)
      if (this.eventTypes.length === 0) {
        return false
      }
      this.currentType = this.eventTypes[0].type
      await this.fetchPageContent()
    },
    async fetchEventTypes() {
      try {
        this.eventTypes = await eventApi.getEventType(this.businessId)
      } catch(err) {
        renderError(err)
      }
    },
    async fetchPageContent() {
      if (this.loading) {
        return false
      }

      this.loading = true

      try {
        if (this.isReserved) {
          await this.fetchReservedEvents()
        } else {
          await this.fetchEvent()
        }
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    },
    async fetchEvent() {
      this.event = await eventApi.getEvent(this.businessId, this.currentType)
    },
    async fetchReservedEvents() {
      this.reservedEvents = await eventApi.getReservedEvent(this.businessId)
    },
    async onTabChange() {
      await this.fetchPageContent()

      // タブは存在したが、イベントがなかったときの制御
      // 別ブラウザでの操作を想定
      if (this.isReservable && this.event.date_list.length === 0) {
        await this.init() // 再読み込み
      }
    },
    async reserveComplete() {
      await this.init()
    },
    async reserveCanceled() {
      await this.init()
    },
  },

  async created() {
    await this.init()
  },
}
</script>
